import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Demo from "../components/Demo";
import New from "../components/New";
import Blog from "../components/Blog";
import HomeAbout from "../components/Homeabout";
import FaqSnippet from "../components/FaqSnippet";
import HomeMobileNavbar from "../components/HomeMobileNavbar";
import Fanout from "../components/Fanout";
gsap.registerPlugin(ScrollTrigger);

const Home = () => {
  const navigate = useNavigate();
  const [scrollEnabled, setScrollEnabled] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const homeRootRef = useRef(null);
  const logoRef = useRef(null);
  const heroRef = useRef(null);
  const contentRef = useRef(null);
  const scrollSection1 = useRef(null);

  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    // reset scroll for all browsers
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const doRefresh = () => {
      try {
        ScrollTrigger.refresh(true);
      } catch (e) {}
    };

    let rafId = null;
    let debounceTimer = null;
    let resizeTimer = null;
    let lastRefreshAt = 0;

    const scheduleRefresh = (delay = 0) => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const now = Date.now();
        if (now - lastRefreshAt < 220) return;
        lastRefreshAt = now;
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(doRefresh);
      }, delay);
    };

    const onLoad = () => {
      scheduleRefresh();
      setTimeout(() => scheduleRefresh(), 200);
    };

    const onResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => scheduleRefresh(), 120);
    };

    const onContentUpdated = () => scheduleRefresh(50);

    const t1 = setTimeout(() => scheduleRefresh(), 100);
    const t2 = setTimeout(() => scheduleRefresh(), 300);
    const t3 = setTimeout(() => scheduleRefresh(), 800);

    window.addEventListener("load", onLoad);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    window.addEventListener("home:content-updated", onContentUpdated);

    const root = homeRootRef.current;
    if (root) {
      // Capture image/video resource loads within Home sections.
      root.addEventListener("load", onContentUpdated, true);
    }

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      if (debounceTimer) clearTimeout(debounceTimer);
      if (resizeTimer) clearTimeout(resizeTimer);
      if (rafId) cancelAnimationFrame(rafId);

      window.removeEventListener("load", onLoad);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      window.removeEventListener("home:content-updated", onContentUpdated);

      if (root) {
        root.removeEventListener("load", onContentUpdated, true);
      }
    };
  }, [scrollEnabled]);

  useEffect(() => {
    // Only run logo animation on larger screens
    if (windowWidth < 1168) {
      document.body.style.overflow = "";
      document.body.style.height = "";
      setScrollEnabled(true);
      return;
    }

    // Disable scroll initially
    document.body.style.overflow = "hidden";
    document.body.style.height = "100vh";

    let scrollAmount = 0;
    const maxScroll = 150;
    let animationFrame = null;
    let isComplete = false;

    const logo = logoRef.current;
    const navbar = document.querySelector(".navbar-logo-wrapper-home");
    const navbarImg = navbar?.querySelector("img");

    if (!logo || !navbar || !navbarImg) return;

    // Function to get accurate navbar position
    const getNavbarPosition = () => {
      const rect = navbarImg.getBoundingClientRect();
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
    };

    const startX = window.innerWidth / 2;
    const startY = window.innerHeight / 2;

    // Set initial position
    gsap.set(logo, {
      position: "fixed",
      left: startX,
      top: startY,
      xPercent: -50,
      yPercent: -50,
      scale: 1,
      zIndex: 10000,
    });

    const handleWheel = (e) => {
      e.preventDefault();

      if (isComplete) return;

      // Increase scroll amount
      scrollAmount += Math.abs(e.deltaY) * 0.3;
      scrollAmount = Math.min(scrollAmount, maxScroll);

      // Calculate progress (0 to 1)
      const progress = scrollAmount / maxScroll;

      // Get current navbar position (in case of any shifts)
      const navbarPos = getNavbarPosition();
      const targetX = navbarPos.x;
      const targetY = navbarPos.y;

      // Cancel previous animation frame
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      // Use GSAP for smooth animation
      animationFrame = requestAnimationFrame(() => {
        const targetScale = 1 - 0.39 * progress;
        const currentX = startX + (targetX - startX) * progress;
        const currentY = startY + (targetY - startY) * progress;

        gsap.to(logo, {
          duration: 0.3,
          ease: "power2.out",
          left: currentX,
          top: currentY,
          scale: targetScale,
          xPercent: -50,
          yPercent: -50,
        });
      });

      // Hide navbar logo during animation
      navbar.style.opacity = "0";

      // Check for completion
      if (progress >= 0.99 && !isComplete) {
        isComplete = true;

        setTimeout(() => {
          // Hide center logo, show navbar logo
          gsap.set(logo, { visibility: "hidden" });
          navbar.style.opacity = "1";

          document.body.style.overflow = "";
          document.body.style.height = "";
          window.removeEventListener("wheel", handleWheel);
          setScrollEnabled(true);
        }, 300);
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      window.removeEventListener("wheel", handleWheel);
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, [windowWidth]);

  // Scroll-based animations using GSAP ScrollTrigger
  // Scroll-based animations using GSAP ScrollTrigger
  useEffect(() => {
    if (!scrollEnabled) return;

    // Horizontal scroll animation - Images slide in from behind center image
    if (scrollSection1.current) {
      // --- FIX 1: GET NAVBAR HEIGHT ---
      // Get the navbar element (using the same selector as your intro animation)
      const navbar = document.querySelector(".navbar-logo-wrapper-home");
      // Get its height, provide a fallback (e.g., 80px) if it's not found
      const navbarHeight = navbar ? navbar.offsetHeight : 80;
      // --- END OF FIX 1 ---

      const leftImage = scrollSection1.current.querySelector(".left-image");
      const rightImage = scrollSection1.current.querySelector(".right-image");
      const productInfos =
        scrollSection1.current.querySelectorAll(".product-info");

      // Create timeline
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: scrollSection1.current,
          // --- FIX 1 (CONTINUED): SET NEW START POSITION ---
          // Start when the top of the section hits the BOTTOM of the navbar
          start: `top ${navbarHeight}px`,
          // --- END OF FIX 1 ---
          end: "+=2000", // Shorter scroll for quicker animation
          scrub: 1,
          pin: true,
          anticipatePin: 1,
        },
      });

      // --- FIX 2: USE RESPONSIVE 'vw' UNITS ---
      // Phase 1: Left image slides out from center to left
      tl.fromTo(
        leftImage,
        { x: 0, opacity: 1 },
        // Use '-33.33vw' to move it left by one-third of the viewport width
        { x: "-33.33vw", opacity: 1, duration: 1, ease: "power2.out" },
        0,
      );

      // Phase 2: Right image slides out from center to right (same time as left)
      tl.fromTo(
        rightImage,
        { x: 0, opacity: 1 },
        // Use '33.33vw' to move it right by one-third of the viewport width
        { x: "33.33vw", opacity: 1, duration: 1, ease: "power2.out" },
        0,
      );
      // --- END OF FIX 2 ---

      // Phase 3: Show buttons (This is correct and will work as you want)
      // The .product-info is already inside each image div, so it will
      // move with its parent image.
      tl.to(
        productInfos,
        { opacity: 1, duration: 0.3, stagger: 0 },
        1, // Starts right when images finish moving
      );

      // Refresh ScrollTrigger to ensure downstream triggers (like HomeAbout)
      // recalculate their positions after this pin-spacer is added.
      setTimeout(() => ScrollTrigger.refresh(), 100);
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [scrollEnabled]);

  return (
    <div ref={homeRootRef} className="relative" style={{ zIndex: 5 }}>
      {/* Mobile Navbar for small screens */}
      {windowWidth < 1168 && <HomeMobileNavbar />}

      {/* Centered Logo (appears on load) */}
      {windowWidth >= 1168 && (
        <div
          ref={logoRef}
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            transition: "opacity 0.3s",
          }}
        >
          <img
            width="200"
            loading="lazy"
            alt="Nufab Logo"
            src="/images/Logo--White.svg"
          />
        </div>
      )}

      {/* Hero Section */}
      <section ref={heroRef} className="section_hero">
        <div className="hero-background">
          <div className="background-video w-background-video w-background-video-atom">
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              poster="/videos/hero-poster.jpg"
              style={{
                backgroundImage:
                  'url("/videos/hero-poster.jpg")',
              }}
            >
              <source src="/videos/hero.mp4" type="video/mp4" />
            </video>
            <div className="video-overlay"></div>
          </div>
        </div>

        {/* CTA - Shop Store, centered over the video, just below the Nufab logo */}
        <div
          className="absolute left-1/2 -translate-x-1/2 z-10"
          style={{ top: 'calc(50% + 70px)' }}
        >
          <button
            type="button"
            onClick={() => navigate('/store')}
            style={{ border: '1px solid #000', boxSizing: 'border-box', backgroundColor: '#fff' }}
            className="group text-black bg-white px-6 py-3 flex items-center justify-center text-[15px] font-semibold tracking-wide font-bdogrotesk whitespace-nowrap"
          >
            <div className="relative h-5 overflow-hidden">
              <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                <span className="flex h-5 items-center">Shop Store</span>
                <span className="flex h-5 items-center">Shop Store</span>
              </div>
            </div>
          </button>
        </div>
      </section>

      {/* Section 02 - Mission Statement */}
      <div ref={contentRef} className="bg-white py-12 overflow-hidden">
        <div className="w-screen">
          <h1
            className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bdogrotesk font-medium leading-[1.0] text-black px-2 md:px-4 lg:px-4 "
            style={{ letterSpacing: "-0.028em" }}
          >
            We make everyday clothes for people who like dressing up everyday.
            We free culture of the shackles of nostalgia, and let it take its
            place in today's everyday - in your everyday.
          </h1>
        </div>
        {/* Vision section - column on mobile, horizontal on desktop */}
        <div className="mt-16 px-2 md:px-4 lg:px-4 flex flex-row justify-between items-start md:relative md:items-start">
          <h3
            className="text-xs md:text-base font-bdogrotesk font-medium text-black tracking-tighter leading-[1.0] md:absolute "
            style={{ letterSpacing: "-0.028em" }}
          >
            To Preserve *
          </h3>
          <p
            className=" text-xs md:text-base font-bdogrotesk font-medium text-black text-left w-[58%] md:w-[31vw] md:mx-auto leading-[1.2]"
            style={{ letterSpacing: "-0.028em" }}
          >
            * and Evolve <br />
            We see local materials, motifs, and skills as invaluable design
            resources. To honour them means keeping them alive in practice, and
            letting them take shape with time. Culture does not belong in a
            museum to be occasionally recalled with nostalgia. It belongs where
            it was born: with the people, in the everyday.
          </p>
        </div>
      </div>

      {/* Horizontal Scroll Section - Material Driven */}
 <Fanout key={scrollEnabled ? 'unlocked' : 'locked'} />

      {/* ... rest of component unchanged ... */}

      {/* Section 03 - About Us Cards */}
      {/* <div className="home-section-03">
        <div className="home-section-03-about-us">
          <div className="rt-portfolio-marquee">
            <div className="rt-portfolio-marquee-block">
              <div className="rt-collection-list-four">
                <div className="rt-service-three-portfolio-wrapper rt-underline-off rt-background-dark">
                  <img
                    className="rt-autofit-cover rt-responsive-full-width-image rt-change-height"
                    src="/images/Experimental-Marketing_1Experimental Marketing.webp"
                    width="478"
                    height="595"
                    alt="experimental marketing"
                    loading="lazy"
                  />
                  <div className="rt-service-three-portfolio-overlay"></div>
                  <div className="rt-service-three-portfolio-text">
                    <Link to="/about" className="button w-button">
                      About Us
                    </Link>
                  </div>
                </div>
                <div className="rt-service-three-portfolio-wrapper rt-underline-off rt-background-dark">
                  <img
                    className="rt-autofit-cover rt-responsive-full-width-image rt-change-height"
                    src="/images/Group-262_1Group 262.webp"
                    width="478"
                    height="595"
                    alt="mobile image"
                    loading="lazy"
                  />
                  <div className="rt-service-three-portfolio-overlay"></div>
                  <div className="rt-service-three-portfolio-text">
                    <Link to="/contact" className="button w-button">
                      Contact
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* ... remaining sections unchanged ... */}

      <div className="  mx-2 pt-8 md:pt-16">
        <div className=" ">
          <h1 className="heading01 text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-black">
            Discover
            <br />
            Collection
          </h1>
        </div>
        <Demo />
      </div>

      <div className="bg-white py-24 overflow-hidden">
        <div className="w-screen">
          <h1
            className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bdogrotesk font-medium leading-[1.0] text-black px-4 md:px-4 lg:px-4"
            style={{ letterSpacing: "-0.028em" }}
          >
            No brand strategy, no personality tax. We’re passing on craft without making it a luxury.
          </h1>
        </div>
      </div>
      <New />
      <Blog />
      <HomeAbout />
      <FaqSnippet />
    </div>
  );
};

export default Home;







    //  <div
    //     ref={scrollSection1}
    //     className="hidden md:block min-h-screen bg-white overflow-hidden relative "
    //   >
    //     <div className="horizontal-scroll-container h-screen flex items-center justify-center relative ">
    //       {/* Fixed Text on sides - BEHIND images */}
    //       <div
    //         className="home-body-text fixed left-8 top-1/2 -translate-y-1/2 text-sm font-bdogrotesk font-medium z-0 leading-[1.0]"
    //         style={{ letterSpacing: "-0.028em" }}
    //       >
    //         Culture
    //       </div>
    //       <div
    //         className="home-body-text fixed right-8 top-1/2 -translate-y-1/2 text-sm font-bdogrotesk font-medium z-0 leading-[1.0]"
    //         style={{ letterSpacing: "-0.028em" }}
    //       >
    //         Driven
    //       </div>

    //       {/* Image Container - All 3 images stacked, will spread to full width */}
    //       <div className="images-wrapper font-bdogrotesk-medium relative w-full h-full flex items-center justify-center">
    //         {/* Left Image - Slides to left edge, fills 1/3 width */}
    //         <div
    //           className="left-image absolute left-1/2 -translate-x-1/2 w-[33.33vw]"
    //           style={{ zIndex: 10 }}
    //         >
    //           <img
    //             src="/images/Experimental-Marketing_1Experimental Marketing.webp"
    //             alt="Arco Shelf Metal"
    //             className="w-full h-[75vh] object-cover"
    //           />
    //           <div className="product-info mt-6 opacity-0 ">
    //             <h3 className="home-body-text text-base font-medium mb-3">Arco Shelf Metal</h3>
    //             {/* --- NEW ANIMATED BUTTON --- */}
    //             <button
    //               type="button"
    //               onClick={() => navigate("/product/1")}
    //               className="group mt-2 w-full bg-black text-white px-3 py-3 flex items-center justify-between text-[15px] font-semibold tracking-wide"
    //             >
    //               <div className="relative h-5 overflow-hidden">
    //                 <div className="home-body-text relative flex flex-col font-medium text-base transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
    //                   <span className="flex h-5 items-center">Shop now</span>
    //                   <span className="flex h-5 items-center">Shop now</span>
    //                 </div>
    //               </div>
    //               <div className="relative h-5 overflow-hidden">
    //                 <div className="relative flex flex-col font-medium text-base transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
    //                   <span className="flex h-5 items-center text-white/90">
    //                     $ 4,200.00 USD
    //                   </span>
    //                   <span className="flex h-5 items-center text-white/90">
    //                     $ 4,200.00 USD
    //                   </span>
    //                 </div>
    //               </div>
    //             </button>
    //           </div>
    //         </div>

    //         {/* Center Image - Stays in center, fills 1/3 width */}
    //         <div
    //           className="center-image absolute left-1/2 -translate-x-1/2 w-[33.33vw]"
    //           style={{ zIndex: 20 }}
    //         >
    //           <img
    //             src="/images/Group-262_1Group 262.webp"
    //             alt="Arco Sofa Metal"
    //             className="w-full h-[75vh] object-cover"
    //           />
    //           <div className="product-info mt-6 opacity-0 ml-0.5">
    //             <h3 className="home-body-text text-base font-medium mb-3">Arco Sofa Metal</h3>
    //             {/* --- NEW ANIMATED BUTTON --- */}
    //             <button
    //               type="button"
    //               onClick={() => navigate("/product/1")}
    //               className="group mt-2 w-full font-medium text-base bg-black text-white px-3 py-3 flex items-center justify-between  tracking-wide"
    //             >
    //               <div className="relative h-5 overflow-hidden">
    //                 <div className="home-body-text relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
    //                   <span className="flex h-5 items-center">Shop now</span>
    //                   <span className="flex h-5 items-center">Shop now</span>
    //                 </div>
    //               </div>
    //               <div className="relative h-5 overflow-hidden">
    //                 <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
    //                   <span className="flex h-5 items-center text-white/90">
    //                     $ 7,500.00 USD
    //                   </span>
    //                   <span className="flex h-5 items-center text-white/90">
    //                     $ 7,500.00 USD
    //                   </span>
    //                 </div>
    //               </div>
    //             </button>
    //           </div>
    //         </div>

    //         {/* Right Image - Slides to right edge, fills 1/3 width */}
    //         <div
    //           className="right-image absolute left-1/2 -translate-x-1/2 w-[33.33vw]"
    //           style={{ zIndex: 10 }}
    //         >
    //           <img
    //             src="/images/Group-1009002131-1_1Group 1009002131 (1).webp"
    //             alt="Arco Stool Metal"
    //             className="w-full h-[75vh] object-cover"
    //           />
    //           <div className="product-info mt-6 opacity-0 ml-0.5 ">
    //             <h3 className="home-body-text text-base font-medium mb-3">Arco Stool Metal</h3>
    //             {/* --- NEW ANIMATED BUTTON --- */}
    //             <button
    //               type="button"
    //               onClick={() => navigate("/product/1")}
    //               className="group mt-2 w-full bg-black text-white px-3 py-3 font-medium text-base flex items-center justify-between tracking-wide"
    //             >
    //               <div className="relative h-5 overflow-hidden">
    //                 <div className="home-body-text relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
    //                   <span className="flex h-5 items-center">Shop now</span>
    //                   <span className="flex h-5 items-center">Shop now</span>
    //                 </div>
    //               </div>
    //               <div className="relative h-5 overflow-hidden">
    //                 <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
    //                   <span className="flex h-5 items-center text-white/90">
    //                     $ 1,500.00 USD
    //                   </span>
    //                   <span className="flex h-5 items-center text-white/90">
    //                     $ 1,500.00 USD
    //                   </span>
    //                 </div>
    //               </div>
    //             </button>
    //           </div>
    //         </div>
    //       </div>
    //     </div>
    //   </div>